import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
    Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle,
    RotateCcw, History, AlertCircle, Search, Filter, ArrowRight
} from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { PAYMENT_METHODS } from '../lib/constants';
import { formatCurrency, formatDate } from '@/lib/format';
import { toast } from 'sonner';

export default function HistoricalImportModal({ open, onOpenChange, onSuccess }) {
    const [viewMode, setViewMode] = useState('import'); // 'import' or 'history'
    const [uploading, setUploading] = useState(false);
    const [batch, setBatch] = useState(null);
    const [filterTab, setFilterTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [jobs, setJobs] = useState([]);
    const [jobSearchQuery, setJobSearchQuery] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [batchesHistory, setBatchesHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [sameCustomerPrompt, setSameCustomerPrompt] = useState(null);

    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open && viewMode === 'history') {
            fetchBatchesHistory();
        }
    }, [open, viewMode]);

    // Debounced server-side job search. Results REPLACE the list (no merging),
    // so the dropdown always reflects the latest /jobs/lookup response.
    const jobReqId = useRef(0);
    useEffect(() => {
        if (!open) return;
        const reqId = ++jobReqId.current;
        const t = setTimeout(async () => {
            try {
                const q = jobSearchQuery.trim();
                const params = q ? { q, limit: 50 } : { limit: 50 };
                const res = await api.get('/jobs/lookup', { params });
                if (reqId === jobReqId.current) setJobs(res.data || []);
            } catch (err) {
                console.error('Failed to load jobs', err);
            }
        }, jobSearchQuery ? 275 : 0);
        return () => clearTimeout(t);
    }, [open, jobSearchQuery]);

    const fetchBatchesHistory = async () => {
        setLoadingHistory(true);
        try {
            const res = await api.get('/collections/imports');
            setBatchesHistory(res.data || []);
        } catch (err) {
            toast.error(formatApiError(err, 'İçe aktarma geçmişi yüklenemedi.'));
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            toast.error('Lütfen geçerli bir .xlsx dosyası seçin.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        try {
            const res = await api.post('/collections/imports/preview', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setBatch(res.data);
            if (res.data.status === 'completed') {
                toast.info('Bu dosya daha önce başarıyla içe aktarılmış. Geçmiş kayıt gösteriliyor.');
            } else {
                toast.success('Dosya önizleme için başarıyla yüklendi.');
            }
        } catch (err) {
            toast.error(formatApiError(err, 'Dosya incelenirken hata oluştu.'));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRowUpdate = async (rowId, updateData) => {
        if (!batch) return;
        if (batch.status === 'completed' || batch.status === 'undone') {
            toast.error("Bu içe aktarma grubu tamamlanmış veya geri alınmıştır, kayıtlar değiştirilemez.");
            return;
        }
        try {
            const res = await api.patch(`/collections/imports/${batch.id}/rows/${rowId}`, updateData);
            const updatedRow = res.data;

            // Update in local state
            setBatch(prev => {
                if (!prev) return prev;
                let newRows;
                if (updateData.apply_to_same_customer) {
                    newRows = prev.rows.map(r => {
                        if (r.normalized_customer_name === updatedRow.normalized_customer_name) {
                            return {
                                ...r,
                                matched_job_id: updateData.matched_job_id !== undefined ? updateData.matched_job_id : r.matched_job_id,
                                matched_job_number: updatedRow.matched_job_number,
                                matched_job_customer: updatedRow.matched_job_customer,
                                matching_status: updateData.matched_job_id ? 'matched' : r.matching_status,
                                user_decision: updateData.user_decision !== undefined ? updateData.user_decision : r.user_decision,
                                override_payment_method: updateData.override_payment_method !== undefined ? updateData.override_payment_method : r.override_payment_method,
                                is_resolved: true
                            };
                        }
                        return r;
                    });
                } else {
                    newRows = prev.rows.map(r => r.id === rowId ? updatedRow : r);
                }
                return { ...prev, rows: newRows };
            });

            toast.success('Satır güncellendi.');
        } catch (err) {
            toast.error(formatApiError(err, 'Satır güncellenemedi.'));
        }
    };

    const handleConfirmImport = async () => {
        if (!batch) return;
        setConfirming(true);
        try {
            const res = await api.post(`/collections/imports/${batch.id}/confirm`);
            toast.success(`Başarıyla ${res.data.imported_rows} adet tahsilat içe aktarıldı (${formatCurrency(res.data.total_amount)}).`);
            setBatch(null);
            onOpenChange(false);
            if (onSuccess) onSuccess();
        } catch (err) {
            toast.error(formatApiError(err, 'İçe aktarma onaylanamadı.'));
        } finally {
            setConfirming(false);
        }
    };

    const handleUndoBatch = async (batchId) => {
        if (!window.confirm('Bu gruptaki tüm içe aktarılan tahsilatlar silinecektir. Devam etmek istiyor musunuz?')) {
            return;
        }

        try {
            const res = await api.post(`/collections/imports/${batchId}/undo`);
            toast.success(`İçe aktarma geri alındı. ${res.data.undone_collections_count} tahsilat silindi.`);
            fetchBatchesHistory();
            if (batch && batch.id === batchId) {
                setBatch(null);
            }
            if (onSuccess) onSuccess();
        } catch (err) {
            toast.error(formatApiError(err, 'Geri alma işlemi başarısız oldu.'));
        }
    };

    const handleOpenBatch = async (batchId) => {
        try {
            const res = await api.get(`/collections/imports/${batchId}`);
            setBatch(res.data);
            setViewMode('import');
            toast.info('İçe aktarma grubu detayları yüklendi.');
        } catch (err) {
            toast.error(formatApiError(err, 'Grup yüklenemedi.'));
        }
    };

    const handleSelectJob = (row, jobId) => {
        if (!jobId || jobId === 'none') {
            handleRowUpdate(row.id, { matched_job_id: null, apply_to_same_customer: false });
            return;
        }
        const sameCustRows = batch?.rows?.filter(
            r => r.id !== row.id && r.normalized_customer_name === row.normalized_customer_name
        ) || [];

        if (sameCustRows.length > 0) {
            setSameCustomerPrompt({
                rowId: row.id,
                jobId: jobId,
                customerName: row.raw_customer_name,
                sameCount: sameCustRows.length
            });
        } else {
            handleRowUpdate(row.id, { matched_job_id: jobId, apply_to_same_customer: false });
        }
    };

    // Row filtering
    const rows = batch?.rows || [];
    const filteredRows = rows.filter(r => {
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            const cust = (r.raw_customer_name || '').toLowerCase();
            const desc = (r.raw_description || '').toLowerCase();
            const amt = r.raw_amount?.toString() || '';
            if (!cust.includes(q) && !desc.includes(q) && !amt.includes(q)) return false;
        }

        const flags = JSON.parse(r.review_flags || '[]');

        if (filterTab === 'matched') return r.matching_status === 'matched';
        if (filterTab === 'ambiguous') return r.matching_status === 'ambiguous';
        if (filterTab === 'unmatched') return r.matching_status === 'unmatched';
        if (filterTab === 'duplicates') return r.duplicate_status !== 'none';
        if (filterTab === 'review') return !r.is_resolved || flags.length > 0;
        if (filterTab === 'unresolved') return !r.is_resolved;
        if (filterTab === 'skipped') return r.user_decision === 'skip';

        return true;
    });

    // Summary calculations
    const importSelectedRows = rows.filter(r => r.user_decision === 'import');
    const selectedTotalAmount = importSelectedRows.reduce((acc, r) => acc + parseFloat(r.raw_amount || 0), 0);
    const unassignedJobCount = rows.filter(r => r.user_decision === 'import' && !r.matched_job_id).length;
    const unresolvedReviewCount = rows.filter(r => !r.is_resolved).length;
    const blockingCount = unassignedJobCount + unresolvedReviewCount;
    const isReadOnly = Boolean(batch && (batch.status === 'completed' || batch.status === 'undone'));

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl max-h-[92vh] flex flex-col p-6 overflow-hidden">
                <DialogHeader className="flex flex-row justify-between items-start">
                    <div>
                        <DialogTitle className="text-xl font-bold flex items-center gap-2 text-gp-graphite">
                            <FileSpreadsheet className="w-5 h-5 text-gp-blue" />
                            Geçmiş Tahsilatları İçe Aktar (Excel)
                        </DialogTitle>
                        <DialogDescription className="text-sm text-gray-500 mt-1">
                            Geçmiş tahsilat Excel tablosunu yükleyin, iş eşleştirmelerini ve mükerrer kayıtları kontrol edip onaylayın.
                        </DialogDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant={viewMode === 'import' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('import')}
                            className={viewMode === 'import' ? 'bg-brand-blue hover:bg-brand-blue-hover text-white' : ''}
                        >
                            İçe Aktar
                        </Button>
                        <Button
                            variant={viewMode === 'history' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setViewMode('history')}
                            className={viewMode === 'history' ? 'bg-brand-blue hover:bg-brand-blue-hover text-white' : ''}
                        >
                            <History className="w-4 h-4 mr-1.5" />
                            Geçmiş Gruplar
                        </Button>
                    </div>
                </DialogHeader>

                {viewMode === 'history' ? (
                    <div className="flex-1 overflow-y-auto mt-4 space-y-4">
                        {loadingHistory ? (
                            <div className="text-center py-12 text-gray-500">Geçmiş yükleniyor...</div>
                        ) : batchesHistory.length === 0 ? (
                            <div className="text-center py-12 text-gray-500 border rounded-lg bg-gray-50">
                                Henüz içe aktarılmış bir grup bulunmuyor.
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Dosya Adı</TableHead>
                                        <TableHead>Tarih</TableHead>
                                        <TableHead>İçe Aktaran</TableHead>
                                        <TableHead>Durum</TableHead>
                                        <TableHead className="text-right">Aktarılan Satır</TableHead>
                                        <TableHead className="text-right">Toplam Tutar</TableHead>
                                        <TableHead className="text-right">İşlem</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {batchesHistory.map(b => (
                                        <TableRow key={b.id}>
                                            <TableCell className="font-medium">{b.original_filename}</TableCell>
                                            <TableCell>{formatDate(b.created_at)}</TableCell>
                                            <TableCell>{b.creator_name || '-'}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={
                                                    b.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                    b.status === 'undone' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                                                    'bg-amber-50 text-amber-700 border-amber-200'
                                                }>
                                                    {b.status === 'completed' ? 'Tamamlandı' :
                                                     b.status === 'undone' ? 'Geri Alındı' :
                                                     b.status === 'preview' ? 'Önizleme' : b.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right font-medium">{b.imported_rows_count}</TableCell>
                                            <TableCell className="text-right font-semibold text-emerald-600">
                                                {formatCurrency(b.imported_total_amount)}
                                            </TableCell>
                                            <TableCell className="text-right space-x-1.5 whitespace-nowrap">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 px-2.5 text-xs border-slate-300 text-slate-700 hover:text-slate-900 hover:bg-slate-50 font-medium"
                                                    onClick={() => handleOpenBatch(b.id)}
                                                    data-testid={`batch-open-${b.id}`}
                                                >
                                                    Görüntüle
                                                </Button>
                                                {b.status === 'completed' && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 px-2.5 text-xs text-rose-700 hover:text-rose-800 border-rose-200 bg-rose-50/60 hover:bg-rose-100 font-medium"
                                                        onClick={() => handleUndoBatch(b.id)}
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                                        Geri Al
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 overflow-hidden flex flex-col mt-3 space-y-4">
                        {!batch ? (
                            <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl p-8 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                                <FileSpreadsheet className="w-12 h-12 text-gray-400 mb-3" />
                                <h3 className="font-semibold text-base text-gray-800">Tahsilat Raporu Excel Dosyasını Yükleyin</h3>
                                <p className="text-xs text-gray-500 mt-1 max-w-md text-center">
                                    Beklenen başlıklar: Tarih, Evrak cinsi, Hareket cinsi, Açıklama, Cari Hesap İsmi, Cari Hesap Alacak
                                </p>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept=".xlsx"
                                    className="hidden"
                                    onChange={handleFileSelect}
                                />
                                <Button
                                    className="mt-5 bg-brand-blue hover:bg-brand-blue-hover text-white"
                                    disabled={uploading}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    {uploading ? 'İnceleniyor...' : 'Excel Dosyası Seç (.xlsx)'}
                                </Button>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col overflow-hidden space-y-3">
                                {/* Summary KPIs */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                                    <Card className="bg-slate-50 border-slate-200">
                                        <CardContent className="p-2.5">
                                            <p className="text-[11px] text-gray-500">Okunan / Geçerli</p>
                                            <p className="text-base font-bold text-slate-800">{batch.total_rows} Satır</p>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-emerald-50 border-emerald-200">
                                        <CardContent className="p-2.5">
                                            <p className="text-[11px] text-emerald-700">Otomatik Eşleşen</p>
                                            <p className="text-base font-bold text-emerald-800">{batch.matched_rows}</p>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-amber-50 border-amber-200">
                                        <CardContent className="p-2.5">
                                            <p className="text-[11px] text-amber-700">Belirsiz (Çoklu İş)</p>
                                            <p className="text-base font-bold text-amber-800">{batch.ambiguous_rows}</p>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-rose-50 border-rose-200">
                                        <CardContent className="p-2.5">
                                            <p className="text-[11px] text-rose-700">Eşleşmeyen</p>
                                            <p className="text-base font-bold text-rose-800">{batch.unmatched_rows}</p>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-purple-50 border-purple-200">
                                        <CardContent className="p-2.5">
                                            <p className="text-[11px] text-purple-700">Mükerrer Adayı</p>
                                            <p className="text-base font-bold text-purple-800">{batch.duplicate_rows}</p>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-blue-50 border-blue-200">
                                        <CardContent className="p-2.5">
                                            <p className="text-[11px] text-blue-700">Aktarılacak Tutar</p>
                                            <p className="text-base font-bold text-blue-800">{formatCurrency(selectedTotalAmount)}</p>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Read-only banner */}
                                {isReadOnly && (
                                    <div className="bg-amber-50/90 border border-amber-200 rounded-lg px-3.5 py-2.5 text-xs text-amber-900 flex items-center justify-between shadow-xs">
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                            <span className="font-medium">
                                                {batch.status === 'completed'
                                                    ? 'Bu içe aktarma grubu tamamlanmıştır. Kayıtlar yalnızca görüntülenebilir.'
                                                    : 'Bu içe aktarma grubu geri alınmıştır. Kayıtlar yalnızca görüntülenebilir.'}
                                            </span>
                                        </div>
                                        <span className="text-[11px] font-semibold uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 shrink-0">
                                            Salt Okunur
                                        </span>
                                    </div>
                                )}

                                {/* Filters and Search */}
                                <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                                    <Tabs value={filterTab} onValueChange={setFilterTab} className="w-full sm:w-auto">
                                        <TabsList className="bg-gray-100 p-1">
                                            <TabsTrigger value="all" className="text-xs">Tümü ({rows.length})</TabsTrigger>
                                            <TabsTrigger value="matched" className="text-xs">Eşleşen</TabsTrigger>
                                            <TabsTrigger value="ambiguous" className="text-xs text-amber-700">Belirsiz</TabsTrigger>
                                            <TabsTrigger value="unmatched" className="text-xs text-rose-700">Eşleşmeyen</TabsTrigger>
                                            <TabsTrigger value="duplicates" className="text-xs text-purple-700">Mükerrer</TabsTrigger>
                                            <TabsTrigger value="review" className="text-xs text-blue-700">İnceleme</TabsTrigger>
                                            <TabsTrigger value="skipped" className="text-xs text-gray-500">Atlanan</TabsTrigger>
                                        </TabsList>
                                    </Tabs>

                                    <div className="relative w-full sm:w-64">
                                        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                                        <Input
                                            placeholder="Cari veya açıklama ara..."
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            className="pl-8 h-8 text-xs"
                                        />
                                    </div>
                                </div>

                                {/* Preview Table */}
                                <div className="flex-1 border rounded-lg overflow-y-auto max-h-[46vh] bg-white">
                                    <Table>
                                        <TableHeader className="sticky top-0 bg-gray-50 z-10 shadow-sm">
                                            <TableRow>
                                                <TableHead className="w-12 text-xs">No</TableHead>
                                                <TableHead className="w-24 text-xs">Tarih</TableHead>
                                                <TableHead className="text-xs">Cari Hesap İsmi</TableHead>
                                                <TableHead className="w-28 text-right text-xs">Tutar</TableHead>
                                                <TableHead className="w-32 text-xs">Ödeme Yöntemi</TableHead>
                                                <TableHead className="w-48 text-xs">Eşleşen İş</TableHead>
                                                <TableHead className="text-xs">Durum & İnceleme</TableHead>
                                                <TableHead className="w-24 text-center text-xs">Karar</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredRows.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} className="text-center py-8 text-xs text-gray-500">
                                                        Filtreye uygun satır bulunamadı.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                filteredRows.map(r => {
                                                    const flags = JSON.parse(r.review_flags || '[]');
                                                    const isSkipped = r.user_decision === 'skip';

                                                    return (
                                                        <TableRow key={r.id} className={isSkipped ? 'bg-gray-50/70 opacity-60' : ''}>
                                                            <TableCell className="text-xs font-mono text-gray-400">{r.excel_row_number}</TableCell>
                                                            <TableCell className="text-xs whitespace-nowrap">{formatDate(r.parsed_date)}</TableCell>
                                                            <TableCell>
                                                                <div className="text-xs font-medium text-gray-900">{r.raw_customer_name}</div>
                                                                <div className="text-[10px] text-gray-500 truncate max-w-[200px]" title={r.raw_description}>
                                                                    {r.raw_description || '-'}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-right text-xs font-semibold text-gray-900">
                                                                {formatCurrency(r.raw_amount)}
                                                            </TableCell>
                                                            <TableCell>
                                                                {isReadOnly ? (
                                                                    <span className="text-xs text-slate-700 font-medium">
                                                                        {PAYMENT_METHODS[r.override_payment_method || r.inferred_payment_method] || '-'}
                                                                    </span>
                                                                ) : (
                                                                    <>
                                                                        <Select
                                                                            value={r.override_payment_method || r.inferred_payment_method}
                                                                            onValueChange={v => handleRowUpdate(r.id, { override_payment_method: v })}
                                                                        >
                                                                            <SelectTrigger className="h-7 text-xs">
                                                                                <SelectValue />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                                                                                    <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>
                                                                                ))}
                                                                            </SelectContent>
                                                                        </Select>
                                                                        {r.is_inferred && (
                                                                            <div className="text-[9px] text-amber-600 mt-0.5 truncate" title={r.payment_method_reason}>
                                                                                {r.payment_method_reason}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </TableCell>
                                                             <TableCell>
                                                                {isReadOnly ? (
                                                                    r.matched_job_number ? (
                                                                        <div className="text-xs">
                                                                            <span className="font-mono font-semibold text-slate-900">{r.matched_job_number}</span>
                                                                            <span className="text-slate-500 block text-[11px] truncate max-w-[190px]" title={r.matched_job_customer}>
                                                                                {r.matched_job_customer}
                                                                            </span>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-xs text-slate-400 italic">Eşleşmedi</span>
                                                                    )
                                                                ) : (
                                                                    <>
                                                                        <Select
                                                                            value={r.matched_job_id || 'none'}
                                                                            onValueChange={v => handleSelectJob(r, v === 'none' ? null : v)}
                                                                        >
                                                                            <SelectTrigger className={`h-7 text-xs ${!r.matched_job_id && !isSkipped ? 'border-amber-400 bg-amber-50/50' : ''}`}>
                                                                                <SelectValue placeholder="İş Seçin..." />
                                                                            </SelectTrigger>
                                                                            <SelectContent className="max-h-72">
                                                                                <div className="p-1.5 sticky top-0 bg-white z-10 border-b">
                                                                                    <Input
                                                                                        placeholder="İş no, müşteri ara..."
                                                                                        value={jobSearchQuery}
                                                                                        onChange={e => {
                                                                                            setJobSearchQuery(e.target.value);
                                                                                        }}
                                                                                        className="h-7 text-xs"
                                                                                        onKeyDown={e => e.stopPropagation()}
                                                                                    />
                                                                                </div>
                                                                                <SelectItem value="none" className="text-xs">Seçilmedi</SelectItem>
                                                                                {[
                                                                                    // keep the row's current job visible even if filtered out of results
                                                                                    ...(r.matched_job_id && !jobs.some(j => j.id === r.matched_job_id)
                                                                                        ? [{ id: r.matched_job_id, job_number: r.matched_job_number, customer_name: r.matched_job_customer }]
                                                                                        : []),
                                                                                    ...(r.suggested_job_id && !jobs.some(j => j.id === r.suggested_job_id)
                                                                                        ? [{ id: r.suggested_job_id, job_number: r.suggested_job_number, customer_name: r.suggested_job_customer }]
                                                                                        : []),
                                                                                    ...jobs,
                                                                                ].map(j => (
                                                                                    <SelectItem key={j.id} value={j.id} className="text-xs">
                                                                                        {j.job_number} - {j.customer_name}
                                                                                    </SelectItem>
                                                                                ))}
                                                                            </SelectContent>
                                                                        </Select>
                                                                        {!r.matched_job_id && r.suggested_job_id && !isSkipped && (
                                                                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                                                                                <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded font-medium">
                                                                                    Olası eşleşme: {r.suggested_job_number} - {r.suggested_job_customer}
                                                                                </span>
                                                                                <Button
                                                                                    type="button"
                                                                                    size="sm"
                                                                                    variant="outline"
                                                                                    className="h-5 px-1.5 text-[10px] text-blue-700 border-blue-300 hover:bg-blue-100 font-medium"
                                                                                    onClick={() => handleSelectJob(r, r.suggested_job_id)}
                                                                                >
                                                                                    Öneriyi Kullan
                                                                                </Button>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {r.is_resolved ? (
                                                                        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                                                            Çözümlendi
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800 border-amber-300 font-semibold animate-pulse">
                                                                            İnceleme Gerekli
                                                                        </Badge>
                                                                    )}
                                                                    {r.matching_status === 'matched' && (
                                                                        <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-700 border-slate-200">
                                                                            Eşleşti
                                                                        </Badge>
                                                                    )}
                                                                    {r.matching_status === 'ambiguous' && (
                                                                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                                                            Çoklu İş
                                                                        </Badge>
                                                                    )}
                                                                    {r.matching_status === 'unmatched' && (
                                                                        <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                                                                            İş Bulunamadı
                                                                        </Badge>
                                                                    )}
                                                                    {r.duplicate_status === 'hard_cheque_duplicate' && (
                                                                        <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                                                                            Mükerrer Çek
                                                                        </Badge>
                                                                    )}
                                                                    {r.duplicate_status === 'possible_duplicate' && (
                                                                        <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                                                                            Olası Mükerrer
                                                                        </Badge>
                                                                    )}
                                                                    {flags.includes('avans_iade') && (
                                                                        <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                                                                            Avans İade
                                                                        </Badge>
                                                                    )}
                                                                    {flags.includes('weak_name') && (
                                                                        <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-700 border-gray-200">
                                                                            Tek Kelime İsim
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                {r.duplicate_info && (
                                                                    <div className="text-[9px] text-purple-600 mt-0.5 truncate" title={r.duplicate_info}>
                                                                        {r.duplicate_info}
                                                                    </div>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-center">
                                                                {isReadOnly ? (
                                                                    <Badge variant="outline" className={`text-xs ${isSkipped ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200 font-medium'}`}>
                                                                        {isSkipped ? 'Atlandı' : 'Aktarıldı'}
                                                                    </Badge>
                                                                ) : (
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className={`h-7 px-2.5 text-xs font-medium transition-colors ${
                                                                            isSkipped
                                                                                ? 'border-slate-300 text-slate-700 bg-slate-100 hover:bg-slate-200'
                                                                                : 'border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100'
                                                                        }`}
                                                                        onClick={() => handleRowUpdate(r.id, { user_decision: isSkipped ? 'import' : 'skip' })}
                                                                    >
                                                                        {isSkipped ? 'Atla' : 'Aktar'}
                                                                    </Button>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter className="flex justify-between items-center sm:justify-between border-t pt-3 mt-2">
                    <div className="text-xs text-gray-500">
                        {batch && (
                            <span>
                                {isReadOnly ? (
                                    <span>
                                        Toplam <strong>{batch.total_rows}</strong> satır (Aktarılan: <strong>{batch.imported_rows || 0}</strong>, Atlanan: <strong>{batch.skipped_rows || 0}</strong>)
                                    </span>
                                ) : (
                                    <>
                                        Toplam <strong>{batch.total_rows}</strong> satırdan <strong>{importSelectedRows.length}</strong> tanesi aktarılmak üzere seçildi ({formatCurrency(selectedTotalAmount)}).
                                        {unassignedJobCount > 0 && (
                                            <span className="text-amber-600 ml-2 font-semibold">
                                                ⚠️ {unassignedJobCount} adet satırın iş seçimi yapılmadı!
                                            </span>
                                        )}
                                        {unresolvedReviewCount > 0 && (
                                            <span className="text-rose-600 ml-2 font-semibold">
                                                ⚠️ {unresolvedReviewCount} adet incelenmemiş satır var (Onaylamak için her birini karar verip inceleyin)!
                                            </span>
                                        )}
                                    </>
                                )}
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} size="sm">
                            {isReadOnly ? 'Kapat' : 'Vazgeç'}
                        </Button>
                        {!isReadOnly && batch && (
                            <Button
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                size="sm"
                                disabled={confirming || blockingCount > 0 || importSelectedRows.length === 0}
                                onClick={handleConfirmImport}
                            >
                                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                                {confirming ? 'Aktarılıyor...' : `İçe Aktarımı Onayla (${importSelectedRows.length} Satır)`}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Same customer assignment prompt dialog */}
        <Dialog open={!!sameCustomerPrompt} onOpenChange={(open) => !open && setSameCustomerPrompt(null)}>
            <DialogContent className="max-w-md p-6 bg-white border border-[#DCE2E8] shadow-lg">
                <DialogHeader>
                    <DialogTitle className="text-base font-semibold text-[#171A1F]">
                        Aynı Müşteriye Ait Satırlar Bulundu
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-600 mt-2">
                        Bu dosyada <strong>"{sameCustomerPrompt?.customerName}"</strong> adına ait {sameCustomerPrompt?.sameCount} adet daha satır var. Bu iş eşleştirmesini nasıl uygulamak istersiniz?
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-[#DCE2E8]">
                    <Button
                        variant="outline"
                        className="text-xs border-[#DCE2E8] text-slate-700 hover:bg-slate-50"
                        onClick={() => {
                            if (!sameCustomerPrompt) return;
                            handleRowUpdate(sameCustomerPrompt.rowId, {
                                matched_job_id: sameCustomerPrompt.jobId,
                                apply_to_same_customer: false
                            });
                            setSameCustomerPrompt(null);
                        }}
                    >
                        Sadece Bu Satıra Uygula
                    </Button>
                    <Button
                        className="text-xs bg-brand-blue hover:bg-brand-blue-hover text-white shadow-sm font-medium"
                        onClick={() => {
                            if (!sameCustomerPrompt) return;
                            handleRowUpdate(sameCustomerPrompt.rowId, {
                                matched_job_id: sameCustomerPrompt.jobId,
                                apply_to_same_customer: true
                            });
                            setSameCustomerPrompt(null);
                        }}
                    >
                        Aynı Carinin Tüm Satırlarına Uygula
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </>
    );
}
